import { clientLogger } from '@/lib/clientLogger';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { apiService } from '@/lib/api';
import { ContactDTO, ContactCreateDTO, ContactUpdateDTO, JobContactAssignmentDTO, JobContactInputDTO, JobDTO } from '@/types';
import { ContactFormModal } from './contacts/ContactFormModal';
import { ContactSearchModal } from './contacts/ContactSearchModal';
import { JobContactInfoModal } from './contacts/JobContactInfoModal';
import { X, Plus, Search, Phone, MessageSquare, Trash2, User, Edit2, Contact } from 'lucide-react';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { SmsMethodSelector } from './SmsMethodSelector';
import { CallMethodSheet } from './CallMethodSheet';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';

interface JobContactsMobileModalProps {
    isOpen: boolean;
    onClose: () => void;
    job: JobDTO;
    onJobUpdated?: (job: JobDTO) => void;
    onPastJobSelect?: (jobId: string) => void;
    /** Close the job itself — Contact Info opens estimates/leads as page-level overlays. */
    onLeaveJob?: () => void;
}

export function JobContactsMobileModal({ isOpen, onClose, job, onJobUpdated, onPastJobSelect, onLeaveJob }: JobContactsMobileModalProps) {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { canEditField } = useJobEditPermissions();
    // phone_masked is technician-only; hide_contact_phone is job-level and must not block admin/secretary.
    const canEditContacts = canEditField('contacts') && !job.contacts?.some(c => c.contact.phone_masked);

    const [isContactFormOpen, setContactFormOpen] = useState(false);
    const [isSearchModalOpen, setSearchModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [contactFormMode, setContactFormMode] = useState<'create' | 'edit'>('create');
    const [selectedAssignment, setSelectedAssignment] = useState<JobContactAssignmentDTO | null>(null);
    const [assignmentPendingDetach, setAssignmentPendingDetach] = useState<JobContactAssignmentDTO | null>(null);

    // SMS selector state
    const [smsSelectorState, setSmsSelectorState] = useState<{
        isOpen: boolean;
        recipientPhone: string;
        recipientName: string;
        jobId?: string;
        masked?: boolean;
        contactId?: string;
        contactWhich?: 'primary' | 'additional';
    }>({ isOpen: false, recipientPhone: '', recipientName: '' });

    // Call sheet state
    const [callSheetState, setCallSheetState] = useState<{
        isOpen: boolean;
        toE164: string;
        displayName?: string;
        masked?: boolean;
        contactId?: string;
        contactWhich?: 'primary' | 'additional';
    }>({ isOpen: false, toE164: '' });

    // Contact Info modal: the contact whose tab is showing, null when closed
    const [contactInfoId, setContactInfoId] = useState<string | null>(null);
    const layer = useModalLayer({
        enabled: isOpen && !isSearchModalOpen && !contactInfoId && !smsSelectorState.isOpen && !assignmentPendingDetach,
        onClose,
        historyState: { jobContactsModal: true },
    });

    // Attach contact to job mutation
    const attachContactMutation = useMutation({
        mutationFn: async (payload: JobContactInputDTO) => {
            return apiService.attachContactToJob(job.id, payload);
        },
        onSuccess: async () => {
            await refreshJobDetails();
            addNotification(notificationService.success('Contact added to job'));
        },
        onError: (error: any) => {
            clientLogger.error('Failed to attach contact', error);
            addNotification(notificationService.error('Failed to add contact', error.message ?? 'Please try again.'));
        },
    });

    // Detach contact from job mutation
    const detachContactMutation = useMutation({
        mutationFn: async (contactId: string) => apiService.detachContactFromJob(job.id, contactId),
        onSuccess: async () => {
            await refreshJobDetails();
            addNotification(notificationService.success('Contact removed from job'));
        },
        onError: (error: any) => {
            clientLogger.error('Failed to remove job contact', error);
            addNotification(notificationService.error('Failed to remove contact', error.message ?? 'Please try again.'));
        },
    });

    // Update job-contact assignment mutation
    const updateJobContactMutation = useMutation({
        mutationFn: async ({ contactId, payload }: { contactId: string; payload: { notes?: string } }) =>
            apiService.updateJobContact(job.id, contactId, payload),
        onSuccess: async () => {
            await refreshJobDetails();
        },
        onError: (error: any) => {
            clientLogger.error('Failed to update job contact', error);
            addNotification(notificationService.error('Failed to update contact', error.message ?? 'Please try again.'));
        },
    });

    const refreshJobDetails = async () => {
        try {
            const updatedJob = await apiService.getJobById(job.id);
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            queryClient.invalidateQueries({ queryKey: ['job', job.id] });
            if (onJobUpdated) {
                onJobUpdated(updatedJob);
            }
        } catch (error) {
            clientLogger.error('Failed to refresh job data', error);
        }
    };

    const handleCreateNewContact = () => {
        setSelectedAssignment(null);
        setContactFormMode('create');
        setContactFormOpen(true);
    };

    const handleEditContact = (assignment: JobContactAssignmentDTO) => {
        setSelectedAssignment(assignment);
        setContactFormMode('edit');
        setContactFormOpen(true);
    };

    const handleSelectExistingContact = () => {
        setSearchModalOpen(true);
    };

    const handleContactResolution = async (resolution: import('./contacts/ContactFormModal').ContactResolution) => {
        try {
            setIsSubmitting(true);
            switch (resolution.action) {
                case 'create': {
                    const createdContact = await apiService.createContact({
                        name: resolution.contact.name,
                        phone: resolution.contact.phone,
                        additional_phone: resolution.contact.additional_phone,
                        email: resolution.contact.email,
                        notes: resolution.contact.notes,
                    });
                    await attachContactMutation.mutateAsync({
                        id: createdContact.id,
                        notes: resolution.assignmentNotes,
                    });
                    break;
                }
                case 'useExisting': {
                    await attachContactMutation.mutateAsync({
                        id: resolution.contactId,
                        notes: resolution.assignmentNotes,
                    });
                    break;
                }
                case 'createNew': {
                    if (selectedAssignment) {
                        const contactId = selectedAssignment.id || selectedAssignment.contact.id;
                        await detachContactMutation.mutateAsync(contactId);
                        const createdContact = await apiService.createContact({
                            name: resolution.contact.name,
                            phone: resolution.contact.phone,
                            additional_phone: resolution.contact.additional_phone,
                            email: resolution.contact.email,
                            notes: resolution.contact.notes,
                        });
                        await attachContactMutation.mutateAsync({
                            id: createdContact.id,
                            notes: resolution.assignmentNotes,
                        });
                    }
                    break;
                }
            }
            setContactFormOpen(false);
        } catch (error: any) {
            clientLogger.error('Contact resolution failed', error);
            addNotification(
                notificationService.error('Failed to save contact', error.message ?? 'Please try again.')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleContactSubmit = async ({
        contact,
        assignmentNotes,
    }: {
        contact: ContactCreateDTO | ContactUpdateDTO;
        assignmentNotes?: string;
    }) => {
        try {
            setIsSubmitting(true);
            if (contactFormMode === 'create') {
                const createdContact: ContactDTO = await apiService.createContact({
                    name: contact.name ?? '',
                    phone: contact.phone ?? '',
                    additional_phone: (contact as any).additional_phone,
                    email: contact.email,
                    notes: contact.notes,
                });
                await attachContactMutation.mutateAsync({
                    id: createdContact.id,
                    notes: assignmentNotes,
                });
            } else if (contactFormMode === 'edit' && selectedAssignment) {
                const contactId = selectedAssignment.id || selectedAssignment.contact.id;
                await apiService.updateContact(contactId, contact as ContactUpdateDTO);
                await updateJobContactMutation.mutateAsync({
                    contactId,
                    payload: { notes: assignmentNotes },
                });
            }
            setContactFormOpen(false);
        } catch (error: any) {
            clientLogger.error('Contact form submission failed', error);
            addNotification(
                notificationService.error('Failed to save contact', error.message ?? 'Please try again.')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleExistingContactSelect = async (contact: ContactDTO) => {
        setSearchModalOpen(false);
        try {
            await attachContactMutation.mutateAsync({
                id: contact.id,
            });
        } catch {
            // Error handling already performed in mutation onError
        }
    };

    const requestRemoveContact = (assignment: JobContactAssignmentDTO) => {
        setAssignmentPendingDetach(assignment);
    };

    const confirmRemoveContactFromJob = async () => {
        if (!assignmentPendingDetach) return;
        const assignment = assignmentPendingDetach;
        const contactId = assignment.id || assignment.contact.id;
        if (!contactId) {
            throw new Error('Contact ID is required for removal');
        }
        await detachContactMutation.mutateAsync(contactId);
    };

    if (!isOpen) return null;

    const contacts = job.contacts ?? [];

    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
        <div className="fixed inset-0 flex flex-col bg-white safe-area-top" style={layer.style}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={layer.onClose}
                        className="p-2 -ml-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Contacts</h2>
                        <p className="text-xs text-gray-500">
                            {contacts.length} contact{contacts.length !== 1 ? 's' : ''} assigned
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {contacts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <User className="h-12 w-12 text-gray-300 mb-3" />
                        <p className="text-sm font-medium">No contacts assigned</p>
                        <p className="text-xs text-gray-400 mt-1">Add a contact to this job</p>
                    </div>
                ) : (
                    contacts.map((assignment) => (
                        <div
                            key={assignment.id || assignment.contact.id}
                            className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                            {assignment.contact.name || 'No contact name'}
                                        </p>
                                    </div>
                                    {assignment.contact.phone && (
                                        <p className="text-xs text-gray-500">
                                            {assignment.contact.phone_masked
                                                ? assignment.contact.phone
                                                : formatPhoneNumber(assignment.contact.phone)}
                                        </p>
                                    )}
                                    {assignment.contact.additional_phone && (
                                        <p className="text-xs text-gray-500">
                                            {assignment.contact.phone_masked
                                                ? assignment.contact.additional_phone
                                                : formatPhoneNumber(assignment.contact.additional_phone)}
                                        </p>
                                    )}
                                    {assignment.contact.email && (
                                        <p className="text-xs text-gray-400 truncate">{assignment.contact.email}</p>
                                    )}
                                    {assignment.notes && (
                                        <p className="text-xs text-gray-500 mt-2 italic">"{assignment.notes}"</p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-1 ml-3">
                                    {/* Edit Button */}
                                    {canEditContacts && (
                                    <button
                                        type="button"
                                        onClick={() => handleEditContact(assignment)}
                                        className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                                        title="Edit contact"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setContactInfoId(assignment.contact.id)}
                                        className="p-2 bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 transition-colors"
                                        title="Contact info"
                                    >
                                        <Contact className="h-4 w-4" />
                                    </button>
                                    {assignment.contact.phone && (
                                        <>
                                            {assignment.contact.phone_masked ? (
                                                // Masked: the sheet dials at once with one line, or offers the choice
                                                <button
                                                    type="button"
                                                    onClick={() => setCallSheetState({
                                                        isOpen: true,
                                                        toE164: assignment.contact.phone!,
                                                        displayName: assignment.contact.name || 'Client',
                                                        masked: true,
                                                        contactId: assignment.contact.id,
                                                        contactWhich: 'primary',
                                                    })}
                                                    className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                                                >
                                                    <Phone className="h-4 w-4" />
                                                </button>
                                            ) : (
                                                // Unmasked: open call method sheet
                                                <button
                                                    type="button"
                                                    onClick={() => setCallSheetState({
                                                        isOpen: true,
                                                        toE164: assignment.contact.phone!,
                                                        displayName: assignment.contact.name || formatPhoneNumber(assignment.contact.phone!),
                                                        masked: false,
                                                    })}
                                                    className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                                                >
                                                    <Phone className="h-4 w-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setSmsSelectorState({
                                                    isOpen: true,
                                                    recipientPhone: assignment.contact.phone!,
                                                    recipientName: assignment.contact.name || 'Client',
                                                    jobId: job.id,
                                                    masked: assignment.contact.phone_masked,
                                                    contactId: assignment.contact.id,
                                                    contactWhich: 'primary',
                                                })}
                                                className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                                            >
                                                <MessageSquare className="h-4 w-4" />
                                            </button>
                                        </>
                                    )}
                                    {/* Additional phone SMS button — only available when masked */}
                                    {assignment.contact.additional_phone && assignment.contact.phone_masked && (
                                        <button
                                            type="button"
                                            onClick={() => setSmsSelectorState({
                                                isOpen: true,
                                                recipientPhone: assignment.contact.additional_phone!,
                                                recipientName: assignment.contact.name || 'Client',
                                                jobId: job.id,
                                                masked: true,
                                                contactId: assignment.contact.id,
                                                contactWhich: 'additional',
                                            })}
                                            className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                                            title="SMS additional number"
                                        >
                                            <MessageSquare className="h-4 w-4" />
                                        </button>
                                    )}
                                    {canEditContacts && (
                                    <button
                                        type="button"
                                        onClick={() => requestRemoveContact(assignment)}
                                        disabled={detachContactMutation.isPending}
                                        className="p-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors disabled:opacity-50"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Footer - Add Contact Actions */}
            <div className="border-t border-gray-200 p-4 pb-[calc(1rem+var(--safe-area-inset-bottom))] bg-white sticky bottom-0 space-y-2 keyboard-aware-footer">
                <button
                    type="button"
                    onClick={handleCreateNewContact}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
                >
                    <Plus className="h-5 w-5" />
                    Create New Contact
                </button>
                <button
                    type="button"
                    onClick={handleSelectExistingContact}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
                >
                    <Search className="h-5 w-5" />
                    Select Existing Contact
                </button>
            </div>

            {/* Sub-Modals */}
            <ContactFormModal
                isOpen={isContactFormOpen}
                onClose={() => setContactFormOpen(false)}
                onSubmit={handleContactSubmit}
                onResolution={handleContactResolution}
                onJobSelect={(jobId) => {
                    if (onPastJobSelect) {
                        onClose();
                        onPastJobSelect(jobId);
                    }
                }}
                companyId={job.company?.id}
                contact={contactFormMode === 'edit' ? selectedAssignment?.contact : undefined}
                initialAssignmentNotes={contactFormMode === 'edit' ? selectedAssignment?.notes : undefined}
                title={contactFormMode === 'edit' ? 'Edit Contact' : 'New Contact'}
                confirmLabel={contactFormMode === 'edit' ? 'Save Changes' : 'Create & Add to Job'}
                isSubmitting={isSubmitting || attachContactMutation.isPending || updateJobContactMutation.isPending}
                includeAssignmentNotes
            />

            <ContactSearchModal
                isOpen={isSearchModalOpen}
                onClose={() => setSearchModalOpen(false)}
                onSelect={handleExistingContactSelect}
            />

            {/* SMS Method Selector */}
            <SmsMethodSelector
                isOpen={smsSelectorState.isOpen}
                onClose={() => setSmsSelectorState({ ...smsSelectorState, isOpen: false })}
                recipientPhone={smsSelectorState.recipientPhone}
                recipientName={smsSelectorState.recipientName}
                recipientType="Client"
                jobId={smsSelectorState.jobId}
                masked={smsSelectorState.masked}
                contactId={smsSelectorState.contactId}
                contactWhich={smsSelectorState.contactWhich}
            />

            {/* Call Method Sheet (used for masked contacts) */}
            <CallMethodSheet
                isOpen={callSheetState.isOpen}
                onClose={() => setCallSheetState({ ...callSheetState, isOpen: false })}
                toE164={callSheetState.toE164}
                displayName={callSheetState.displayName}
                jobId={job.id}
                masked={callSheetState.masked}
                contactId={callSheetState.contactId}
                contactWhich={callSheetState.contactWhich}
            />

            {contactInfoId && (
                <JobContactInfoModal
                    jobId={job.id}
                    contacts={contacts.map((c) => c.contact)}
                    contactId={contactInfoId}
                    onSelectContact={setContactInfoId}
                    onClose={() => setContactInfoId(null)}
                    onBeforeNavigate={() => onLeaveJob?.()}
                />
            )}

            <ConfirmDialog
                isOpen={!!assignmentPendingDetach}
                onClose={() => setAssignmentPendingDetach(null)}
                onConfirm={confirmRemoveContactFromJob}
                isLoading={detachContactMutation.isPending}
                title="Remove contact from job?"
                message={
                    <div className="space-y-2">
                        <span>{destructiveCopy.removeJobContact}</span>
                        <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
                    </div>
                }
                confirmText="Remove"
            />
        </div>
        </LayerDepthContext.Provider>,
        document.body
    );
}
